/**
 * ORBI API Service
 * Handles all API calls for ORBI device management
 * NOTE: ORBI APIs use device ID (not serial number like BR500)
 */
import apiCall from '../hooks/useApi';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// Coalesces concurrent identical summary requests into one network call. On the
// device page, OrbiDeviceProvider and the Summary/DeviceMode tabs all fetch the
// summary at mount; sharing the in-flight promise avoids the duplicate requests.
// The entry is cleared once settled, so a later call (e.g. Summary's refresh)
// fetches fresh.
const inFlightSummary = new Map();

/**
 * Fetch ORBI device summary/dashboard information.
 * The summary URL's {_Id} (the backend userId path param) is substituted by
 * useApi from the _Id cookie, so it is not passed here; coalescing is keyed on
 * deviceId plus that same cookie so concurrent callers share one request.
 * @param {string} deviceId - Device ID (not serial number)
 * @returns {Promise<Object>} API response with device info, statistics, port status
 */
export const fetchOrbiSummary = (deviceId) => {
  const userId = cookies.get('_Id');
  // useApi builds the URL's {_Id} from the _Id cookie; without it (and without a
  // deviceId) the request would hit `.../deviceInfo/undefined/.../dashboard/undefined`,
  // which the backend rejects. Fail fast so no caller issues a bad request.
  if (!deviceId || !userId) {
    return Promise.reject(new Error('fetchOrbiSummary: missing deviceId or _Id cookie'));
  }

  const key = `${deviceId}|${userId}`;
  const existing = inFlightSummary.get(key);
  if (existing) return existing;

  const request = (async () => {
    try {
      return await apiCall('GET_ORBI_SUMMARY', { selectedDeviceId: deviceId }, {}, []);
    } finally {
      // Clear once settled (success or failure) so a later call issues a fresh
      // request rather than resolving from a stale/rejected promise. Callers log
      // the failure, so we don't log-and-rethrow here.
      inFlightSummary.delete(key);
    }
  })();

  inFlightSummary.set(key, request);
  return request;
};

/**
 * Fetch connected clients for ORBI device
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {string} page - Optional pagination parameter
 * @returns {Promise<Object>} API response with client list
 */
/**
 * Fetch ORBI client count (connected/disconnected)
 * @param {string} userId - User ID
 * @param {string} orgId - Organization ID
 * @param {string} networkId - Network ID
 * @param {string} serialNo - Device serial number
 * @returns {Promise<Object>} API response with client counts
 */
export const fetchOrbiClientCount = async (userId, orgId, networkId, serialNo) => {
  try {
    const data = await apiCall(
      'GET_ORBI_CLIENT_COUNT',
      {
        _Id: userId,
        orgId,
        networkId,
        serialNo
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI client count:', error);
    throw error;
  }
};

/**
 * Fetch ORBI client list with filters
 * @param {string} userId - User ID
 * @param {string} orgId - Organization ID
 * @param {string} networkId - Network ID
 * @param {string} serialNo - Device serial number
 * @param {number} pageNo - Page number (0-based)
 * @param {string} isConnected - Connection status (1=connected, 0=disconnected)
 * @param {string} connectionType - Connection type (0=all, 1=wired, 2=wireless)
 * @param {string} searchText - Search text
 * @param {Object} filters - Filter object with deviceList, ssidList, modelList
 * @returns {Promise<Object>} API response with client list
 */
export const fetchOrbiClientList = async (userId, orgId, networkId, serialNo, pageNo = 1, isConnected = '1', connectionType = '0', searchText = '', filters = {}) => {
  try {
    const data = await apiCall(
      'GET_ORBI_CLIENT_LIST',
      {
        _Id: userId,
        orgId,
        networkId,
        serialNo,
        pageNo,
        isConnected,
        connectionType,
        searchText: searchText || ''
      },
      {
        data: {
          deviceList: filters.deviceList || [],
          ssidList: filters.ssidList || [],
          modelList: filters.modelList || []
        }
      },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI client list:', error);
    throw error;
  }
};

/**
 * Fetch ORBI traffic data
 * @param {string} deviceId - Device ID (ObjectId string)
 * @param {string} accountId - Account ID (cookie `accountId`)
 * @param {string} networkId - Network ID (header)
 * @param {string} timeRange - Time range for traffic data
 * @returns {Promise<Object>} API response with traffic data
 */
export const fetchOrbiTrafficData = async (deviceId, accountId, networkId, timeRange = '24h') => {
  try {
    const data = await apiCall(
      'GET_ORBI_TRAFFIC_DATA',
      {
        selectedDeviceId: deviceId,
        timeRange
      },
      {
        headers: { accountId, networkId }
      },
      []
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI traffic data:', error);
    throw error;
  }
};

/**
 * Fetch ORBI WAN IP settings
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with WAN IP configuration
 */
export const fetchOrbiWanIP = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_WAN_IP',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI WAN IP:', error);
    throw error;
  }
};

/**
 * Update ORBI WAN IP settings
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - WAN IP configuration data
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} API response
 */
export const updateOrbiWanIP = async (deviceSerial, accountId, payload, organizationId) => {
  try {
    const data = await apiCall(
      'SET_ORBI_WAN_IP',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        organization_Id: organizationId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI WAN IP:', error);
    throw error;
  }
};

/**
 * Fetch ORBI LAN IP settings
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with LAN IP configuration
 */
export const fetchOrbiLanIP = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_LAN_IP',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI LAN IP:', error);
    throw error;
  }
};

/**
 * Update ORBI device mode (Router/Access Point)
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Device mode configuration
 * @returns {Promise<Object>} API response
 */
export const updateOrbiDeviceMode = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'SET_ORBI_DEVICE_MODE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI device mode:', error);
    throw error;
  }
};

/**
 * Fetch ORBI WiFi network settings
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with WiFi network configuration
 */
export const fetchOrbiWifiNetworks = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_WIFI_NETWORKS',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI WiFi networks:', error);
    throw error;
  }
};

/**
 * Update ORBI WiFi network settings
 * Uses PUT to /device/v2/wirelessnetwork/{networkId}/{deviceId}
 * @param {string} deviceId - Device ID
 * @param {string} networkId - Network ID (passed as _Id in URL)
 * @param {Object} payload - WiFi network configuration
 * @returns {Promise<Object>} API response
 */
export const updateOrbiWifiNetworks = async (deviceId, networkId, payload) => {
  try {
    const data = await apiCall(
      'UPDATE_ORBI_WIFI_NETWORK',
      {
        selectedDeviceId: deviceId,
        _Id: networkId
      },
      { data: payload },  // useApi expects payload wrapped in { data: ... }
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI WiFi networks:', error);
    throw error;
  }
};

/**
 * Fetch ORBI radio and channel settings
 * @param {string} networkId - Network ID
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with radio/channel configuration
 */
export const fetchOrbiRadioChannels = async (networkId, deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_RADIO_CHANNELS',
      {
        networkId,
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI radio channels:', error);
    throw error;
  }
};

/**
 * Update ORBI radio and channel settings
 * @param {string} networkId - Network ID
 * @param {string} deviceSerial - Device Serial Number
 * @param {Object} payload - Radio/channel configuration
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response
 */
export const updateOrbiRadioChannels = async (networkId, deviceSerial, payload, accountId) => {
  try {
    const data = await apiCall(
      'SET_ORBI_RADIO_CHANNELS',
      {
        networkId,
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI radio channels:', error);
    throw error;
  }
};

/**
 * Fetch ORBI WiFi schedules
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @returns {Promise<Object>} API response with WiFi schedules
 */
export const fetchOrbiWifiSchedules = async (deviceSerial, accountId, networkId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_WIFI_SCHEDULES',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        networkId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI WiFi schedules:', error);
    throw error;
  }
};

/**
 * Create ORBI WiFi schedule
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Schedule configuration
 * @returns {Promise<Object>} API response
 */
export const createOrbiWifiSchedule = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'CREATE_ORBI_WIFI_SCHEDULE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error creating ORBI WiFi schedule:', error);
    throw error;
  }
};

/**
 * Update ORBI WiFi schedule
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Schedule configuration
 * @returns {Promise<Object>} API response
 */
export const updateOrbiWifiSchedule = async (deviceSerial, accountId, payload) => {
  try {
    // Uses CREATE endpoint for both create and update operations
    const data = await apiCall(
      'CREATE_ORBI_WIFI_SCHEDULE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI WiFi schedule:', error);
    throw error;
  }
};

/**
 * Delete ORBI WiFi schedule
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Schedule identifier
 * @returns {Promise<Object>} API response
 */
export const deleteOrbiWifiSchedule = async (deviceSerial, accountId, scheduleId, payload) => {
  try {
    const data = await apiCall(
      'DELETE_ORBI_WIFI_SCHEDULE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        scheduleId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting ORBI WiFi schedule:', error);
    throw error;
  }
};

/**
 * Fetch ORBI DHCP server configuration
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with DHCP configuration
 */
export const fetchOrbiDHCPServers = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_DHCP_SERVERS',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI DHCP servers:', error);
    throw error;
  }
};

/**
 * Update ORBI DHCP server configuration
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - DHCP configuration
 * @param {string} commandType - Command type (e.g., 'update', 'create')
 * @returns {Promise<Object>} API response
 */
export const updateOrbiDHCPServer = async (deviceSerial, accountId, payload, commandType) => {
  try {
    const data = await apiCall(
      'SET_ORBI_DHCP_SERVER',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        commandType
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI DHCP server:', error);
    throw error;
  }
};

/**
 * Delete ORBI DHCP server
 * Uses POST to /orbi/v1/dhcpconfig/{_Id}/{selectedDeviceId}/deleteLanSubnet
 * Legacy API requires lanId in header
 * @param {string} deviceId - Device ID
 * @param {string} networkId - Network ID (passed as _Id in URL)
 * @param {Object} dhcpServer - DHCP server object to delete
 * @returns {Promise<Object>} API response
 */
export const deleteOrbiDHCPServer = async (deviceId, networkId, dhcpServer) => {
  if (!dhcpServer?.lanId) {
    console.error('deleteOrbiDHCPServer: lanId is missing from dhcpServer');
    throw new Error('lanId is required to delete DHCP server');
  }
  
  try {
    const payload = {
      dhcpSettingsInfo: dhcpServer
    };
    
    const data = await apiCall(
      'DELETE_ORBI_DHCP_SERVER',
      {
        selectedDeviceId: deviceId,
        _Id: networkId
      },
      { data: payload, headers: { lanId: dhcpServer.lanId } },  // lanId in header per legacy
      ['accountId', 'networkId', 'lanId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting ORBI DHCP server:', error);
    throw error;
  }
};

/**
 * Fetch satellite status for ORBI device
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with satellite status
 */
export const fetchSatelliteStatus = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_SATELLITE_STATUS',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching satellite status:', error);
    throw error;
  }
};

/**
 * Fetch all satellites for ORBI device
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with satellite list
 */
export const fetchSatelliteList = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_SATELLITE_LIST',
      {
        selectedDeviceSerial: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching satellite list:', error);
    throw error;
  }
};

/**
 * Fetch details for a specific satellite
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {string} satelliteSerial - Satellite Serial Number
 * @returns {Promise<Object>} API response with satellite details
 */
export const fetchSatelliteDetails = async (deviceSerial, accountId, satelliteSerial) => {
  try {
    const data = await apiCall(
      'GET_SATELLITE_DETAILS',
      {
        selectedDeviceSerial: deviceSerial,
        _Id: accountId,
        satelliteSerial
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching satellite details:', error);
    throw error;
  }
};

/**
 * Add a new satellite to ORBI device
 * @param {string} accountId - Account ID
 * @param {Object} payload - Satellite configuration
 * @param {string} [networkId] - Orbi device network id (orbiSummaryNetwork). Sent
 *   explicitly so the claim targets the router's network, not the generic
 *   currentNetwork that axiosInstance would otherwise resolve for the header.
 * @returns {Promise<Object>} API response
 */
export const addSatellite = async (accountId, payload, networkId) => {
  try {
    const data = await apiCall(
      'ADD_SATELLITE',
      {
        _Id: accountId
      },
      // Pass networkId via explicit header and drop it from possibleHeaders so the
      // axiosInstance generic networkId resolver doesn't overwrite it.
      { data: payload, ...(networkId ? { headers: { networkId } } : {}) },
      networkId ? ['accountId'] : ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error adding satellite:', error);
    throw error;
  }
};

/**
 * Discover unclaimed satellite devices available to add on the current network.
 * Same backend as legacy portal (device/v2/discoverSatellite/nwDevices/{_Id}).
 * @param {string} accountId - Account ID
 * @param {string} [networkId] - Orbi device network id (orbiSummaryNetwork). Sent
 *   explicitly so discovery scopes to the router's network, not the generic
 *   currentNetwork that axiosInstance would otherwise resolve for the header.
 * @returns {Promise<Object>} API response; discovered devices in `deviceInfo`
 */
export const discoverSatellites = async (accountId, networkId) => {
  try {
    const data = await apiCall(
      'GET_UNCLAIMED_DEVICE_LIST',
      {
        _Id: accountId
      },
      networkId ? { headers: { networkId } } : {},
      networkId ? ['accountId'] : ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error discovering satellites:', error);
    throw error;
  }
};

/**
 * Fetch the wireless neighbor scan (known/unknown APs) that feeds the Outdoor
 * Satellites table. Same backend as legacy portal (device/v2/knownunknownaps/...).
 * @param {string} deviceId - Device ID (selectedDeviceId)
 * @param {string} networkId - Network ID
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response; bands under details.knownAp/unknownAp
 */
export const fetchOrbiOutdoorSatellites = async (deviceId, networkId, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_OUTDOOR_SATELLITES',
      {
        networkId,
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching outdoor satellites (neighbor scan):', error);
    throw error;
  }
};

/**
 * Delete one or more satellites from an ORBI device.
 * Always uses the v2 bulk API with a MAC address array (single delete = one-element array).
 * @param {string} deviceSerial - Router serial number
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {string[]} macAddresses - Satellite MAC address(es)
 * @returns {Promise<Object>} API response
 */
export const deleteSatellites = async (deviceSerial, accountId, networkId, macAddresses) => {
  const macs = (Array.isArray(macAddresses) ? macAddresses : [macAddresses]).filter(Boolean);
  try {
    const data = await apiCall(
      'DELETE_SATELLITES',
      {
        selectedDeviceSerial: deviceSerial,
        _Id: accountId
      },
      {
        data: macs,
        headers: {
          networkId
        }
      },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting satellite(s):', error);
    throw error;
  }
};

/**
 * Rename a satellite
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {Object} payload - Rename configuration
 * @returns {Promise<Object>} API response
 */
export const renameSatellite = async (accountId, networkId, payload) => {
  try {
    const data = await apiCall(
      'RENAME_DEVICE',
      {
        _Id: accountId,
        networkId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error renaming satellite:', error);
    throw error;
  }
};

/**
 * Fetch ORBI VLAN network list
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with VLAN list
 */
export const fetchOrbiVlanList = async (deviceId, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_VLAN_LIST',
      {
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI VLAN list:', error);
    throw error;
  }
};

/**
 * Fetch ORBI network setup details for a specific VLAN
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} vlanId - VLAN ID
 * @returns {Promise<Object>} API response with network setup details
 */
export const fetchOrbiNetworkSetupDetails = async (deviceId, accountId, vlanId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_NETWORK_SETUP_DETAILS',
      {
        selectedDeviceId: deviceId,
        _Id: accountId,
        vlanId: vlanId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI network setup details:', error);
    throw error;
  }
};

/**
 * Create or update an ORBI VLAN network setup.
 * Mirrors the legacy "saveVLANdetails" POST to
 * orbi/v1/vlanNetworkOverhaulConfig/{_Id}/{selectedDeviceId}/{commandType}.
 * @param {string} deviceId - ORBI device ID (NOT serial)
 * @param {('createVlan'|'updateVlan')} commandType - Operation type
 * @param {Object} payload - { vlanSettingsInfo, dhcpSettingsInfo } (same shape as legacy)
 * @returns {Promise<Object>} API response
 */
export const saveOrbiNetworkSetup = async (deviceId, commandType, payload) => {
  try {
    const data = await apiCall(
      'SET_ORBI_NETWORK_SETUP_DETAILS',
      {
        selectedDeviceId: deviceId,
        commandType
      },
      { data: payload }, // useApi reads the request body from payload.data
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error saving ORBI network setup:', error);
    throw error;
  }
};

/**
 * Delete ORBI VLAN
 * Uses POST to /orbi/v1/vlanNetworkOverhaulConfig/{_Id}/{selectedDeviceId}/deleteVlan
 * Legacy API requires vlanId in header, not body
 * @param {string} deviceId - Device ID
 * @param {string} networkId - Network ID (passed as _Id in URL)
 * @param {string} vlanId - VLAN ID to delete
 * @returns {Promise<Object>} API response
 */
export const deleteOrbiVlan = async (deviceId, networkId, vlanId) => {
  try {
    const data = await apiCall(
      'DELETE_ORBI_VLAN',
      {
        selectedDeviceId: deviceId,
        _Id: networkId
      },
      { data: {}, headers: { vlanId: vlanId } },  // vlanId goes in header per legacy
      ['accountId', 'networkId', 'vlanId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting ORBI VLAN:', error);
    throw error;
  }
};

/**
 * Fetch ORBI static routes
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with static routes
 */
export const fetchOrbiStaticRoutes = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_STATIC_ROUTES',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI static routes:', error);
    throw error;
  }
};

/**
 * Add ORBI static route
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Static route configuration
 * @returns {Promise<Object>} API response
 */
export const addOrbiStaticRoute = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'ADD_ORBI_STATIC_ROUTE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error adding ORBI static route:', error);
    throw error;
  }
};

/**
 * Update ORBI static route
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Static route configuration
 * @returns {Promise<Object>} API response
 */
export const updateOrbiStaticRoute = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'UPDATE_ORBI_STATIC_ROUTE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI static route:', error);
    throw error;
  }
};

/**
 * Delete ORBI static route
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Static route identifier
 * @returns {Promise<Object>} API response
 */
export const deleteOrbiStaticRoute = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'DELETE_ORBI_STATIC_ROUTE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting ORBI static route:', error);
    throw error;
  }
};

/**
 * Update ORBI firmware
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {Object} payload - Firmware update configuration
 * @returns {Promise<Object>} API response
 */
export const updateOrbiFirmware = async (deviceSerial, accountId, payload) => {
  try {
    const data = await apiCall(
      'UPDATE_ORBI_FIRMWARE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI firmware:', error);
    throw error;
  }
};

/**
 * Get ORBI firmware update status
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @returns {Promise<Object>} API response with firmware status
 */
export const getOrbiFirmwareStatus = async (accountId, networkId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_FIRMWARE_STATUS',
      {
        _Id: accountId,
        networkId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error getting ORBI firmware status:', error);
    throw error;
  }
};

/**
 * Get ORBI configuration backup info
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with backup info
 */
export const getOrbiConfigBackup = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_CONFIG_BACKUP',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error getting ORBI config backup:', error);
    throw error;
  }
};

/**
 * Restore ORBI configuration from backup
 * @param {string} accountId - Account ID
 * @param {Object} payload - Restore configuration
 * @returns {Promise<Object>} API response
 */
export const restoreOrbiConfig = async (accountId, payload) => {
  try {
    const data = await apiCall(
      'RESTORE_ORBI_CONFIG',
      {
        _Id: accountId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error restoring ORBI config:', error);
    throw error;
  }
};

/**
 * RMA (Return Merchandise Authorization) - Restore and Replace
 * @param {string} accountId - Account ID
 * @param {Object} payload - RMA configuration
 * @returns {Promise<Object>} API response
 */
export const orbiRmaRestore = async (accountId, payload) => {
  try {
    const data = await apiCall(
      'ORBI_RMA_RESTORE',
      {
        _Id: accountId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error performing ORBI RMA:', error);
    throw error;
  }
};

/**
 * Execute ping test on ORBI device
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {Object} payload - Ping test configuration
 * @returns {Promise<Object>} API response with ping results
 */
export const orbiPingTest = async (deviceSerial, accountId, networkId, payload) => {
  try {
    const data = await apiCall(
      'ORBI_PING_TEST',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        networkId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error executing ORBI ping test:', error);
    throw error;
  }
};

/**
 * Execute DNS lookup test on ORBI device
 * @param {string} accountId - Account ID
 * @param {Object} payload - DNS lookup configuration
 * @returns {Promise<Object>} API response with DNS results
 */
export const orbiDnsLookup = async (accountId, payload) => {
  try {
    const data = await apiCall(
      'ORBI_DNS_LOOKUP',
      {
        _Id: accountId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error executing ORBI DNS lookup:', error);
    throw error;
  }
};

/**
 * Execute speed test on ORBI device
 * @param {string} accountId - Account ID
 * @param {Object} payload - Speed test configuration
 * @returns {Promise<Object>} API response with speed test results
 */
export const orbiSpeedTest = async (accountId, payload) => {
  try {
    const data = await apiCall(
      'ORBI_SPEED_TEST',
      {
        _Id: accountId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error executing ORBI speed test:', error);
    throw error;
  }
};

/**
 * Execute traceroute test on ORBI device
 * @param {string} deviceSerial - Device Serial Number
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {Object} payload - Traceroute configuration
 * @returns {Promise<Object>} API response with traceroute results
 */
export const orbiTraceroute = async (deviceSerial, accountId, networkId, payload) => {
  try {
    const data = await apiCall(
      'ORBI_TRACEROUTE',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId,
        networkId
      },
      payload,
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error executing ORBI traceroute:', error);
    throw error;
  }
};

/**
 * Fetch ORBI WiFi Schedules
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @returns {Promise<Object>} API response with WiFi schedules
 */
export const fetchOrbiWiFiSchedules = async (deviceId, accountId, networkId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_WIFI_SCHEDULES',
      {
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI WiFi schedules:', error);
    throw error;
  }
};

/**
 * Create ORBI WiFi Schedule
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {Object} payload - Schedule data
 * @returns {Promise<Object>} API response
 */
export const createOrbiWiFiSchedule = async (deviceId, accountId, networkId, payload) => {
  try {
    const data = await apiCall(
      'CREATE_ORBI_WIFI_SCHEDULE',
      {
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error creating ORBI WiFi schedule:', error);
    throw error;
  }
};

/**
 * Delete ORBI WiFi Schedule
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {string} scheduleId - Schedule ID to delete
 * @returns {Promise<Object>} API response
 */
export const deleteOrbiWiFiSchedule = async (deviceId, accountId, networkId, scheduleId) => {
  try {
    const data = await apiCall(
      'DELETE_ORBI_WIFI_SCHEDULE',
      {
        selectedDeviceId: deviceId,
        _Id: accountId,
        scheduleId
      },
      {},
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error deleting ORBI WiFi schedule:', error);
    throw error;
  }
};

/**
 * Set ORBI DHCP Server (Create/Update/Delete)
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} networkId - Network ID
 * @param {string} commandType - Command type (createLanSubnet, updateLanSubnet, deleteLanSubnet)
 * @param {Object} payload - DHCP server data
 * @returns {Promise<Object>} API response
 */
export const setOrbiDHCPServer = async (deviceId, accountId, networkId, commandType, payload) => {
  try {
    const data = await apiCall(
      'SET_ORBI_DHCP_SERVER',
      {
        selectedDeviceId: deviceId,
        _Id: accountId,
        commandType
      },
      { data: payload },
      ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error setting ORBI DHCP server:', error);
    throw error;
  }
};

/**
 * Fetch ORBI WAN IP Settings
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with WAN IP settings
 */
export const fetchOrbiWANIP = async (deviceId, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_WAN_IP',
      {
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI WAN IP settings:', error);
    throw error;
  }
};

/**
 * Update ORBI WAN IP Settings
 * @param {string} deviceId - Device ID
 * @param {string} accountId - Account ID
 * @param {string} orgId - Organization ID
 * @param {Object} payload - WAN IP settings
 * @returns {Promise<Object>} API response
 */
export const updateOrbiWANIP = async (deviceId, accountId, orgId, payload) => {
  try {
    const data = await apiCall(
      'SET_ORBI_WAN_IP',
      {
        selectedDeviceId: deviceId,
        _Id: accountId,
        organization_Id: orgId
      },
      { data: payload },
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error updating ORBI WAN IP settings:', error);
    throw error;
  }
};

/**
 * Fetch ORBI Satellite List
 * @param {string} deviceSerial - Device Serial Number (not device ID)
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} API response with satellite list
 */
export const fetchOrbiSatelliteList = async (deviceSerial, accountId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_SATELLITE_LIST',
      {
        selectedDeviceId: deviceSerial,
        _Id: accountId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI satellite list:', error);
    throw error;
  }
};

/**
 * Poll satellite pairing status during the guided Add Satellite flow.
 * Same backend as legacy portal (getSatellite ->
 * device/v2/satellitestatus/{deviceId}/{_Id}).
 * @param {string} deviceId - Orbi router device ID (selectedDeviceId)
 * @param {string} accountId - Account ID (_Id)
 * @param {string} [networkId] - Orbi router network id (sent as header, like discover/add)
 * @returns {Promise<Object>} API response; pairing entries under `info` (status "0"=added, "1"=pairing, "2"=failed)
 */
export const fetchOrbiSatelliteStatus = async (deviceId, accountId, networkId) => {
  try {
    const data = await apiCall(
      'GET_ORBI_SATELLITE_STATUS',
      {
        selectedDeviceId: deviceId,
        _Id: accountId
      },
      networkId ? { headers: { networkId } } : {},
      networkId ? ['accountId'] : ['accountId', 'networkId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching ORBI satellite status:', error);
    throw error;
  }
};

/**
 * Fetch device firmware information
 * @param {string} serialNo - Device serial number
 * @param {string} networkId - Network ID
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} API response with firmware information
 */
export const fetchDeviceFirmwareList = async (serialNo, networkId, orgId) => {
  try {
    const data = await apiCall(
      'GET_SINGLE_DEVICE_FIRMWARE_LIST',
      {
        serialNo,
        networkId,
        _Id: orgId
      },
      {},
      ['accountId']
    );
    return data;
  } catch (error) {
    console.error('Error fetching device firmware list:', error);
    throw error;
  }
};
